import React, {PureComponent} from 'react';
import i18n from '../../../utils/i18n';
import PropTypes from 'prop-types';
import Button from '@adsk/bim360-matrix-react-button';
import Loader from '@adsk/bim360-matrix-react-loader';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import SearchBoxDropdown from '@adsk/bim360-matrix-react-search-box-dropdown';
import ModalConfirmation from '@adsk/bim360-matrix-react-modal-confirmation';
import IssueAttributesAdminTableContainer from './IssueAttributesAdminTableContainer';
import IssueAttributesAdminEditModalContainer from './IssueAttributesAdminEditModalContainer';
import IssueAttributesAdminCreateButton from './IssueAttributesAdminCreateButton';
import IssueAttributesAdminDeleteConfirmationModal from './IssueAttributesAdminDeleteConfirmationModal';
import {FIELD_TRACK_EVENT, issueAttributesAdminVisit} from '../../../utils/analytics-helpers';

class IssueAttributesAdmin extends PureComponent {
  constructor(props) {
    super(props);

    this.searchBoxDropdownInputProps = {
      'data-testid': 'IssueAttributesAdmin__search'
    };

    const tableData = props.data || [];

    this.state = {
      tableData,
      searchExpression: '',
      suggestionList: [],
      simpleDeleteModalOpen: false,
      twoStepDeleteModalOpen: false,
      issueAttributeForDeletion: null
    };

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.editIssueAttribute = this.editIssueAttribute.bind(this);
    this.deleteIssueAttribute = this.deleteIssueAttribute.bind(this);
    this.closeDeleteConfirmationModal = this.closeDeleteConfirmationModal.bind(this);
    this.openDeleteModal = this.openDeleteModal.bind(this);
    this.onAttributeCreate = this.onAttributeCreate.bind(this);
  }

  componentWillMount() {
    // MixPanel Event - when user enters issue attributes admin page
    this.props.trackEvent(issueAttributesAdminVisit(), FIELD_TRACK_EVENT);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.data !== nextProps.data) {
      this.setState({tableData: nextProps.data});
    }
  }

  fetchIssuesCustomizationMetadata() {
    this.props.fetchIssuesCustomizationMetadata();
  }

  getSearchResults(searchExpression) {
    return this.props.data.filter(
      issueAttribute => issueAttribute.title.toLowerCase().indexOf(searchExpression.toLowerCase()) > -1
    );
  }

  onSearch(searchExpression) {
    const searchResults = this.getSearchResults(searchExpression);
    this.setState({tableData: searchResults});
  }

  onSearchResultSelect(result) {
    this.setState({tableData: [result]});
  }

  onSearchChange(searchExpression) {
    this.setState({searchExpression, suggestionList: this.getSearchResults(searchExpression)});
  }

  onSearchClear() {
    this.setState({tableData: this.props.data});
  }

  openDeleteModal(issueAttributeForDeletion, isMapped) {
    return isMapped
      ? this.openTwoStepDeleteConfirmationModal(issueAttributeForDeletion)
      : this.openSimpleDeleteConfirmationModal(issueAttributeForDeletion);
  }

  openSimpleDeleteConfirmationModal(issueAttributeForDeletion) {
    this.setState({issueAttributeForDeletion, simpleDeleteModalOpen: true});
  }

  openTwoStepDeleteConfirmationModal(issueAttributeForDeletion) {
    this.setState({issueAttributeForDeletion, twoStepDeleteModalOpen: true});
  }

  closeDeleteConfirmationModal() {
    this.setState({
      issueAttributeForDeletion: null,
      twoStepDeleteModalOpen: false,
      simpleDeleteModalOpen: false
    });
  }

  deleteIssueAttribute() {
    const {id, title} = this.state.issueAttributeForDeletion;
    this.props.deleteIssueAttributeAction(id, title, {
      onComplete: this.closeDeleteConfirmationModal
    });
  }

  editIssueAttribute(id) {
    this.props.changeLocationAction(`/admin/issueAttributes/${id}`);
  }

  openModal() {
    this.props.changeLocationAction('/admin/issueAttributes/new');
  }

  closeModal() {
    this.props.changeLocationAction('/admin/issueAttributes');
  }

  onAttributeCreate() {
    this.props.scrollDown();
  }

  _renderCreateButton() {
    return (
      <IssueAttributesAdminCreateButton data-testid="IssueAttributesAdmin__create-button" onClick={this.openModal} />
    );
  }

  _renderHeader() {
    return (
      <div className="IssueAttributesAdmin__header">
        <SearchBoxDropdown
          suggestionList={this.state.suggestionList}
          placeholder={i18n.t('issueAttributesAdmin.find')}
          className="IssueAttributesAdmin__search"
          searchExpression={this.state.searchExpression}
          inputProps={this.searchBoxDropdownInputProps}
          onSearch={this.onSearch.bind(this)}
          onClear={this.onSearchClear.bind(this)}
          onSuggestedResultRowSelect={this.onSearchResultSelect.bind(this)}
          searchForSuggestedResults={this.onSearchChange.bind(this)}
        />
        {this._renderCreateButton()}
      </div>
    );
  }

  _renderTable() {
    return (
      <IssueAttributesAdminTableContainer
        tableData={this.state.tableData}
        openCreateModal={this.openModal}
        deleteIssueAttribute={this.openDeleteModal}
        editIssueAttribute={this.editIssueAttribute}
      />
    );
  }

  _renderTryAgain() {
    return (
      <Button
        className="IssueAttributesAdmin__empty-state-try-again"
        data-testid="IssueAttributesAdmin__empty-state-try-again"
        onClick={this.fetchIssuesCustomizationMetadata.bind(this)}
        styleType={Button.StyleType.INLINE_LINK}
      >
        {i18n.t('adminPages.emptyState.tryAgain')}
      </Button>
    );
  }

  _renderErrorEmptyState() {
    return (
      <EmptyState
        icon={EmptyState.Icons.CONE}
        className="IssueAttributesAdmin__empty-state"
        text={i18n.t('adminPages.emptyState.didntLoad')}
        footer={this._renderTryAgain()}
      />
    );
  }

  _renderIssueAttributeModal() {
    return (
      <IssueAttributesAdminEditModalContainer
        container={this}
        onCreate={this.onAttributeCreate}
        closeModal={this.closeModal}
      />
    );
  }

  _renderTwoStepVerificationDeleteModal() {
    const title = this.state.issueAttributeForDeletion ? this.state.issueAttributeForDeletion.title : '';
    return (
      <IssueAttributesAdminDeleteConfirmationModal
        container={this}
        isOpen={this.state.twoStepDeleteModalOpen}
        isDeleting={this.props.isDeleting}
        title={title}
        deleteIssueAttribute={this.deleteIssueAttribute}
        closeModal={this.closeDeleteConfirmationModal}
      />
    );
  }

  _renderSimpleDeleteConfirmationModal() {
    const title = this.state.issueAttributeForDeletion ? this.state.issueAttributeForDeletion.title : '';
    return (
      <ModalConfirmation
        container={this}
        title={i18n.t('issueAttributesAdmin.modal.deleteTitle', {attributeName: title})}
        show={this.state.simpleDeleteModalOpen}
        onConfirm={this.deleteIssueAttribute}
        disableModalExit={this.props.isDeleting}
        isOnConfirmInProgress={this.props.isDeleting}
        confirmationButtonLabelInProgress={i18n.t('issueAttributesAdmin.modal.inProgress')}
        onExited={this.closeDeleteConfirmationModal}
        onHide={this.closeDeleteConfirmationModal}
        message={i18n.t('issueAttributesAdmin.modal.deleteMessage')}
        confirmationButtonLabel={i18n.t('issueAttributesAdmin.modal.delete')}
        confirmationButtonStyleType={Button.StyleType.DANGER}
      />
    );
  }

  _renderNotFoundEmptyState() {
    return (
      <EmptyState
        icon={EmptyState.Icons.NOT_FOUND}
        className="IssueAttributesAdmin__empty-state"
        text={i18n.t('issueAttributesAdmin.emptyState.noMatchingAttributes')}
      />
    );
  }

  render() {
    const {isLoading, error} = this.props;

    if (isLoading) {
      return <Loader />;
    } else if (error) {
      return this._renderErrorEmptyState();
    }

    const hasData = this.props.data.length > 0;
    const searchExpressionNotFound = hasData && this.state.tableData.length === 0;

    return (
      <div className="IssueAttributesAdmin">
        {hasData && this._renderHeader()}
        {searchExpressionNotFound ? this._renderNotFoundEmptyState() : this._renderTable()}
        {this._renderIssueAttributeModal()}
        {this._renderSimpleDeleteConfirmationModal()}
        {this._renderTwoStepVerificationDeleteModal()}
      </div>
    );
  }
}

IssueAttributesAdmin.displayName = 'IssueAttributesAdmin';

IssueAttributesAdmin.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  fetchIssuesCustomizationMetadata: PropTypes.func.isRequired,
  deleteIssueAttributeAction: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
  changeLocationAction: PropTypes.func.isRequired,
  scrollDown: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired
};

export default IssueAttributesAdmin;
